/**
 * Dispute Service
 * Handles dispute data fetching
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockDisputes } from '../mock/mockDisputes';
import { generateDisputeDetail } from '../mock/mockDisputeDetail';
import { delay } from '../mock/mockData';
import { MissionService } from '../mission/MissionService';
import { parseDate } from '../../utils/dateUtils';
import { getCallSignDisplayName } from '../../utils/generalUtils';
import type {
  Dispute,
  DisputeDetail,
  DisputeFilter,
  PaginatedDisputes,
  DisputeStatus,
  DisputeDecision,
  DisputeParty,
} from '../../types/dispute';

/**
 * API response type for dispute item
 */
interface ApiDisputeItem {
  id: string;
  missionId: string;
  missionCode?: string;
  missionTitle?: string;
  assetId: string;
  assetFirstName?: string;
  assetLastName?: string;
  assetCallSign?: string;
  assetEmail?: string;
  ghostId?: string;
  ghostFirstName?: string;
  ghostLastName?: string;
  ghostCallSign?: string;
  ghostEmail?: string;
  type: string;
  status: string; // "Open", "InReview", "Resolved", "Rejected"
  decision: string;
  refundAmount: number;
  createdAt: string;
  handledAt?: string;
}

/**
 * API response type for paginated disputes
 */
interface ApiPaginatedDisputesResponse {
  total: number;
  items: ApiDisputeItem[];
}

/**
 * API response type for dispute detail (from /admin/disputes/{id})
 */
interface ApiDisputeManagementDetails {
  id: string;
  missionId: string;
  assetId: string;
  requestedByUserId: string;
  type: 'Cancel' | 'Refund';
  paymentTransactionId?: string;
  reason: string;
  description?: string;
  cancellationReason?: string;
  status: 'Open' | 'UnderReview' | 'RequestMoreInformation' | 'ResolvedApproved' | 'ResolvedDenied';
  decision: 'None' | 'Full' | 'Partial' | 'Denied';
  justification?: string;
  refundAmount?: number;
  handledBy?: string;
  handledAt?: string;
  notes?: string;
  additionalInformation?: string;
  additionalInformationProvidedAt?: string;
  informationRequestNote?: string;
  informationRequestedBy?: string;
  informationRequestedAt?: string;
  createdAt: string;
  updatedAt: string;
  missionCode: string;
  missionTitle: string;
  assetFirstName?: string;
  assetLastName?: string;
  assetCallSign: string;
  assetEmail?: string;
  ghostId?: string;
  ghostFirstName?: string;
  ghostLastName?: string;
  ghostCallSign?: string;
  ghostEmail?: string;
  paymentTransactionAmount?: number;
  taskLogs?: Array<{
    id: string;
    type: string;
    title: string;
    description: string;
    timestamp: string;
    userId?: string;
  }>;
  evidence?: Array<{
    id: string;
    fileName: string;
    fileUrl: string;
    fileType: string;
    uploadedAt?: string;
  }>;
}

/**
 * Map API status to DisputeStatus
 */
const mapApiStatusToDisputeStatus = (apiStatus: string): DisputeStatus => {
  const statusMap: Record<string, DisputeStatus> = {
    'Open': 'underreview',
    'InReview': 'underreview',
    'UnderReview': 'underreview',
    'RequestMoreInformation': 'underreview',
    'Resolved': 'resolved',
    'ResolvedApproved': 'resolved',
    'ResolvedDenied': 'rejected',
    'Rejected': 'rejected',
  };
  return statusMap[apiStatus] || 'underreview';
};

/**
 * Map API decision to DisputeDecision
 */
const mapApiDecisionToDisputeDecision = (
  apiDecision: string,
  apiStatus: string
): DisputeDecision | undefined => {
  // If status is RequestMoreInformation, decision is request_more_info
  if (apiStatus === 'RequestMoreInformation') {
    return 'request_more_info';
  }

  if (!apiDecision || apiDecision === 'None') {
    return undefined;
  }

  const decisionMap: Record<string, DisputeDecision> = {
    'Full': 'approve_refund',
    'Partial': 'partial_refund',
    'Denied': 'reject_refund',
  };
  return decisionMap[apiDecision];
};

/**
 * Convert API dispute item to Dispute type
 */
const convertApiDisputeToDispute = (apiDispute: ApiDisputeItem): Dispute => {
  const dateOpened = parseDate(apiDispute.createdAt);
  const lastUpdate = apiDispute.handledAt ? parseDate(apiDispute.handledAt) : dateOpened;

  // Format dates as YYYY-MM-DD
  // formatDateText helper removed as redundant

  // Build parties array
  const parties = [];

  // Add asset/client party
  if (apiDispute.assetId) {
    parties.push({
      type: 'client' as const,
      name: getCallSignDisplayName(apiDispute.assetCallSign, 'Asset'),
      email: apiDispute.assetEmail || '',
      id: apiDispute.assetId,
    });
  }

  // Add ghost/agent party
  if (apiDispute.ghostId) {
    parties.push({
      type: 'agent' as const,
      name: getCallSignDisplayName(apiDispute.ghostCallSign, 'Ghost'),
      email: apiDispute.ghostEmail || '',
      id: apiDispute.ghostId,
    });
  }

  return {
    id: apiDispute.id,
    disputeId: apiDispute.id, // Use id as disputeId, or could generate from missionCode
    missionId: apiDispute.missionId,
    missionCode: apiDispute.missionCode,
    missionTitle: apiDispute.missionTitle,
    status: mapApiStatusToDisputeStatus(apiDispute.status),
    dateOpened: dateOpened || new Date(),
    lastUpdate: lastUpdate || dateOpened || new Date(),
    parties,
  };
};

/**
 * Convert API dispute detail to DisputeDetail type
 */
const convertApiDisputeDetailToDisputeDetail = (
  apiData: ApiDisputeManagementDetails
): DisputeDetail => {
  const dateOpened = parseDate(apiData.createdAt);
  const lastUpdate = apiData.handledAt ? parseDate(apiData.handledAt) : parseDate(apiData.updatedAt);
  const createdAt = parseDate(apiData.createdAt);

  // Format dates as YYYY-MM-DD
  // formatDateText helper removed as redundant

  // Build parties array
  const parties: DisputeParty[] = [];

  // Asset (Client)
  if (apiData.assetId) {
    parties.push({
      type: 'client',
      name: getCallSignDisplayName(apiData.assetCallSign, 'Asset'),
      email: apiData.assetEmail || '',
      id: apiData.assetId,
    });
  }

  // Ghost (Agent)
  if (apiData.ghostId) {
    parties.push({
      type: 'agent',
      name: getCallSignDisplayName(apiData.ghostCallSign, 'Ghost'),
      email: apiData.ghostEmail || '',
      id: apiData.ghostId,
    });
  }

  // Determine initiator (who filed the dispute)
  let initiatorId: string | undefined;
  let initiatorType: 'asset' | 'ghost' | undefined;
  let initiatorName: string | undefined;

  if (apiData.requestedByUserId === apiData.assetId) {
    // Initiated by Asset
    initiatorId = apiData.assetId;
    initiatorType = 'asset';
    initiatorName = getCallSignDisplayName(apiData.assetCallSign, 'Asset');
  } else if (apiData.ghostId && apiData.requestedByUserId === apiData.ghostId) {
    // Initiated by Ghost
    initiatorId = apiData.ghostId;
    initiatorType = 'ghost';
    initiatorName = getCallSignDisplayName(apiData.ghostCallSign, 'Ghost');
  }

  // Map mission history types to dispute log types
  const mapLogType = (apiType: string): 'dispute_filed' | 'ai_analyzed' | 'admin_viewed' | 'decision_made' => {
    const lowerType = apiType.toLowerCase();
    if (lowerType.includes('dispute') || lowerType.includes('filed')) return 'dispute_filed';
    if (lowerType.includes('ai') || lowerType.includes('analyzed')) return 'ai_analyzed';
    if (lowerType.includes('admin') || lowerType.includes('viewed')) return 'admin_viewed';
    if (lowerType.includes('decision') || lowerType.includes('approve') || lowerType.includes('reject')) return 'decision_made';
    return 'dispute_filed'; // default
  };


  return {
    id: apiData.id,
    disputeId: apiData.id,
    missionId: apiData.missionId,
    missionTitle: apiData.missionTitle,
    status: mapApiStatusToDisputeStatus(apiData.status),
    dateOpened: dateOpened || new Date(),
    lastUpdate: lastUpdate || parseDate(apiData.updatedAt) || new Date(),
    parties,
    createdAt: createdAt || undefined,
    reason: apiData.reason,
    cancellationReason: apiData.cancellationReason,
    userClaim: apiData.description,
    missionCode: apiData.missionCode,
    initiatorId,
    initiatorType,
    initiatorName,
    decision: mapApiDecisionToDisputeDecision(apiData.decision, apiData.status),
    decisionReason: apiData.justification,
    refundAmount: apiData.refundAmount,
    // disputedAmount comes from payment transaction amount
    disputedAmount: apiData.paymentTransactionAmount && apiData.paymentTransactionAmount > 0
      ? apiData.paymentTransactionAmount
      : undefined,
    // Additional information submitted by user via mobile
    additionalInformation: apiData.additionalInformation,
    additionalInformationProvidedAt: apiData.additionalInformationProvidedAt ? (parseDate(apiData.additionalInformationProvidedAt) || undefined) : undefined,
    // Convert evidence from API
    evidence: apiData.evidence?.map(e => ({
      id: e.id,
      fileName: e.fileName,
      fileUrl: e.fileUrl,
      fileType: (e.fileType === 'image' || e.fileType === 'document' ? e.fileType : 'other') as 'image' | 'document' | 'other',
      uploadedAt: e.uploadedAt ? (parseDate(e.uploadedAt) || undefined) : undefined,
    })),
    // Convert task logs from API
    // Map to same structure as MissionStatusHistory for consistent display
    taskLogs: apiData.taskLogs?.map(log => ({
      id: log.id,
      type: mapLogType(log.type),
      status: log.title || log.type, // Use title from API (backend handles formatting)
      description: log.description,
      timestamp: parseDate(log.timestamp) || new Date(),
      userId: log.userId,
    })),
    // Communication history is not available from API
    communicationHistory: undefined,
    investigationId: undefined,
  };
};

const sanitizeMockDisputeParties = (parties: DisputeParty[]): DisputeParty[] =>
  parties.map((party) => ({
    ...party,
    name: getCallSignDisplayName(undefined, party.type === 'client' ? 'Asset' : 'Ghost'),
  }));

const replaceDisputePartyNames = (
  text: string | undefined,
  parties: DisputeParty[]
): string | undefined => {
  if (!text) {
    return text;
  }

  return parties.reduce((result, party) => {
    const replacement = getCallSignDisplayName(
      undefined,
      party.type === 'client' ? 'Asset' : 'Ghost'
    );

    if (!party.name || party.name === replacement) {
      return result;
    }

    return result.split(party.name).join(replacement);
  }, text);
};

const sanitizeMockDispute = (dispute: Dispute): Dispute => ({
  ...dispute,
  parties: sanitizeMockDisputeParties(dispute.parties),
});

const sanitizeMockDisputeDetail = (dispute: DisputeDetail): DisputeDetail => {
  const originalParties = dispute.parties;
  const sanitizedParties = sanitizeMockDisputeParties(originalParties);
  const assetName =
    sanitizedParties.find((party) => party.type === 'client')?.name || 'Asset';
  const ghostName =
    sanitizedParties.find((party) => party.type === 'agent')?.name || 'Ghost';

  return {
    ...dispute,
    parties: sanitizedParties,
    initiatorName: dispute.initiatorType === 'asset'
      ? assetName
      : dispute.initiatorType === 'ghost'
        ? ghostName
        : replaceDisputePartyNames(dispute.initiatorName, originalParties),
    communicationHistory: dispute.communicationHistory?.map((message) => ({
      ...message,
      senderName: message.sender === 'asset' ? assetName : ghostName,
    })),
    taskLogs: dispute.taskLogs?.map((log) => ({
      ...log,
      description: replaceDisputePartyNames(log.description, originalParties),
      userName: replaceDisputePartyNames(log.userName, originalParties),
    })),
  };
};

class DisputeService {
  /**
   * Get paginated disputes with optional filters
   */
  static async getDisputes(
    page: number = 1,
    pageSize: number = 10,
    filter?: DisputeFilter
  ): Promise<PaginatedDisputes> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      let filteredDisputes = mockDisputes.map(sanitizeMockDispute);

      // Apply status filter
      if (filter?.status && filter.status !== 'all') {
        if (Array.isArray(filter.status)) {
          filteredDisputes = filteredDisputes.filter(
            (dispute) => filter.status!.includes(dispute.status)
          );
        } else {
          filteredDisputes = filteredDisputes.filter(
            (dispute) => dispute.status === filter.status
          );
        }
      }

      // Apply search filter
      if (filter?.search) {
        const searchLower = filter.search.toLowerCase();
        filteredDisputes = filteredDisputes.filter(
          (dispute) =>
            dispute.disputeId.toLowerCase().includes(searchLower) ||
            dispute.missionId.toLowerCase().includes(searchLower) ||
            (dispute.missionTitle?.toLowerCase().includes(searchLower) ?? false) ||
            dispute.parties.some((party) =>
              party.name.toLowerCase().includes(searchLower)
            )
        );
      }

      // Apply date range filter
      if (filter?.dateRange) {
        filteredDisputes = filteredDisputes.filter(
          (dispute) =>
            dispute.dateOpened >= filter.dateRange!.start &&
            dispute.dateOpened <= filter.dateRange!.end
        );
      }

      const total = filteredDisputes.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const data = filteredDisputes.slice(startIndex, startIndex + pageSize);

      return {
        data,
        total,
        page,
        pageSize,
        totalPages,
      };
    } else {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (filter?.status && filter.status !== 'all') {
        if (Array.isArray(filter.status)) {
          filter.status.forEach(s => params.append('status', s));
        } else {
          params.append('status', filter.status);
        }
      }
      if (filter?.search) {
        params.append('search', filter.search);
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: ApiPaginatedDisputesResponse;
        message?: string;
      }>(`/admin/disputes?${params.toString()}`);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch disputes');
      }

      // Convert API response to our format
      const apiData = response.data;
      const convertedDisputes = apiData.items.map(convertApiDisputeToDispute);

      // Enrich disputes with mission titles if missing
      const enrichedData = await Promise.all(
        convertedDisputes.map(async (dispute) => {
          // If missionTitle is already present, use it
          if (dispute.missionTitle) {
            return dispute;
          }

          // Otherwise, fetch mission title from MissionService
          try {
            const mission = await MissionService.getMissionById(dispute.missionId);
            return {
              ...dispute,
              missionTitle: mission?.title || undefined,
            };
          } catch (error) {
            // If mission fetch fails, return dispute without missionTitle
            console.warn(`Failed to fetch mission title for ${dispute.missionId}:`, error);
            return dispute;
          }
        })
      );

      // Calculate totalPages
      const totalPages = Math.ceil(apiData.total / pageSize);

      return {
        data: enrichedData,
        total: apiData.total,
        page,
        pageSize,
        totalPages,
      };
    }
  }

  /**
   * Get dispute by ID
   */
  static async getDisputeById(id: string): Promise<Dispute | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const dispute = mockDisputes.find(
        (item) =>
          item.id === id ||
          item.disputeId === id ||
          item.missionId === id
      );

      return dispute ? sanitizeMockDispute(dispute) : null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: Dispute;
        message?: string;
      }>(`/disputes/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch dispute');
      }

      return response.data || null;
    }
  }

  /**
   * Get dispute detail by ID
   */
  static async getDisputeDetail(id: string): Promise<DisputeDetail | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const detail = generateDisputeDetail(id);
      if (detail) {
        return sanitizeMockDisputeDetail(detail);
      }
      // Fallback: try to find basic dispute
      const dispute = mockDisputes.find(
        (item) => item.id === id || item.disputeId === id || item.missionId === id
      );
      if (dispute) {
        return sanitizeMockDisputeDetail({
          ...dispute,
        });
      }
      return null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: ApiDisputeManagementDetails;
        message?: string;
      }>(`/admin/disputes/${id}`);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch dispute detail');
      }

      // Convert API response to frontend format
      return convertApiDisputeDetailToDisputeDetail(response.data);
    }
  }

  /**
   * Submit dispute decision
   */
  static async submitDecision(
    id: string,
    decision: DisputeDetail['decision'],
    reason: string,
    refundAmount?: number
  ): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return true;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/disputes/${id}/decision`, { decision, reason, refundAmount });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to submit decision');
      }

      return true;
    }
  }
}

export { DisputeService };

